import { Injectable } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, map, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class OAuthServiceService {

  constructor(private oidcSecurityService: OidcSecurityService) { }

  get isLoggedIn(): Observable<boolean> {
    return this.oidcSecurityService.isAuthenticated$
      .pipe(map(result => result.isAuthenticated));
  }

  // Bewusst bei JEDEM Zugriff frisch aus getAccessToken() gelesen (nicht ueber
  // toSignal() zwischengespeichert): getAccessToken() liefert intern im Wesentlichen
  // nur einmal einen Wert (ueber getOpenIDConfiguration()). toSignal() abonniert genau
  // einmal beim Erzeugen dieses Singleton-Services - passiert das frueh (z.B. beim
  // App-Bootstrap, bevor ein Login/2FA-Redirect abgeschlossen ist), friert der
  // Signal-Wert auf dem damaligen (leeren) Token ein und bleibt es fuer den Rest der
  // Browser-Session. Alle Services, die hier `Bearer ${this.oauthService.token}`
  // manuell setzen, schickten dadurch nach dem ersten Login einen leeren Token (401),
  // was ueber den 401-Interceptor einen kompletten zweiten STS-Redirect ausloeste
  // (sichtbar als doppeltes "signin-callback"/"Processing request..."). Fund 2026-08-18.
  get token(): string {
    let currentToken = '';
    this.oidcSecurityService.getAccessToken().subscribe(t => currentToken = t);
    return currentToken;
  }

  get userData(): Observable<any> {
    return this.oidcSecurityService.userData$
      .pipe(map(result => result.userData));
  }

  checkAuth(): Observable<boolean> {
    return this.oidcSecurityService.checkAuth().pipe(map(result => result.isAuthenticated));
  }

  // Verhindert parallele authorize()-Aufrufe: doLogin wird beim unauthentifizierten
  // App-Start von mehreren Stellen fast gleichzeitig gerufen (AppComponent,
  // 401-Interceptor, Route-Guards). Jeder authorize() schreibt einen NEUEN state in
  // den sessionStorage und startet einen eigenen Redirect - der zuletzt gespeicherte
  // state passt dann nicht zum Redirect, der "gewinnt", und der Callback scheitert
  // mit "could not find matching config for state". Die Seite wird durch den
  // Redirect ohnehin entladen, daher genuegt ein einfaches Flag.
  private loginStarted = false;

  // Ein neuer Tab (z.B. kopierte URL) hat noch keine Anmeldung (die Tokens der OIDC-Bibliothek
  // liegen im sessionStorage, der pro Tab getrennt ist) und laeuft einmal ueber den STS. Der
  // signin-callback navigiert danach nach '/' - die urspruengliche Ziel-URL (z.B. /A/all-projects)
  // ginge verloren und der Benutzer landete in der Mandantenliste. Daher wird sie vor dem
  // Redirect fuer genau diesen Tab gemerkt und im signin-callback wiederhergestellt.
  private readonly POST_LOGIN_URL = 'post-login-url';

  doLogin() {
    if (this.loginStarted) {
      return of(void 0);
    }
    this.loginStarted = true;
    this.rememberPostLoginUrl();
    return of(this.oidcSecurityService.authorize());
  }

  private rememberPostLoginUrl(): void {
    try {
      const path = window.location.pathname;
      // Callback-Seiten (auch beim einmaligen Login-Retry) und die Startseite ueberschreiben nichts
      if (path === '/' || /signin-callback|signout|redirect-silentrenew/.test(path)) {
        return;
      }
      sessionStorage.setItem(this.POST_LOGIN_URL, path + window.location.search + window.location.hash);
    } catch { /* sessionStorage nicht verfuegbar: dann eben ohne Wiederherstellung */ }
  }

  // Liefert die vor dem Login gemerkte Ziel-URL (einmalig) oder null.
  consumePostLoginUrl(): string | null {
    try {
      const url = sessionStorage.getItem(this.POST_LOGIN_URL);
      sessionStorage.removeItem(this.POST_LOGIN_URL);
      // nur app-interne Pfade zulassen
      return url && url.startsWith('/') && !url.startsWith('//') ? url : null;
    } catch {
      return null;
    }
  }

  signOut() {
    this.oidcSecurityService.logoff().subscribe();
  }
}
